import { useContext } from "react";
import { stringQueries } from "../../../../../config/filter/CustomFilterConfig";
import {
  FirmaFiltersContext,
  FirmaFiltersEventContext,
} from "../../../../../context/FiltersContext";
import {
  QueryFilter,
  FirmaFilterKeys,
  FirmaVejnavnFilterSetting,
} from "../../../../../interfaces/filters";
import { FirmaDtoFilterInput } from "../../../../../interfaces/schema";
import { f } from "../../../../../util/f";
import { AccordionSubFilterItem } from "../../../../accordion/Accordion";
import AppliedFilterItem from "../../../../filter/AppliedFilterItem";
import SuperDropdown from "../../../../dropdown/SuperDropdown";
import DefaultDropdownItem from "../../../../dropdown/DefaultDropdownItem";
import useVejnavnAutoComplete from "../../../../../hooks/useVejnavnAutoComplete";
import SearchIcon from "../../../../icons/SearchIcon";
import Spinner from "../../../../spinner/Spinner";
import { useCombobox } from "downshift";

// helper
const filterKey: QueryFilter<FirmaFilterKeys>["filterKey"] = "vejnavn";

export default function FirmaVejnavnFilter() {
  // query filter context
  const filters = useContext(FirmaFiltersContext);
  const updateFilters = useContext(FirmaFiltersEventContext);

  // find the filter settings
  const settings: FirmaVejnavnFilterSetting[] =
    filters.find((f) => f.filterKey === filterKey)?.settings || [];

  // preppings
  const selectedQuery = stringQueries[0];

  // vejnavn autocomplete hook
  const { data, isPending, debouncedInputValue, setQuery } =
    useVejnavnAutoComplete();

  // handlers
  function inputEventHandler(inputValue: string) {
    if (!inputValue) {
      return;
    }

    const key: FirmaVejnavnFilterSetting["key"] = `${selectedQuery.query}-${inputValue}`;

    if (settings.some((set) => set.key === key)) {
      alert("Filter er allerede aktivt.");
      return;
    }

    const settingsItem: FirmaVejnavnFilterSetting = {
      key: key,
      selectedQueryName: `${selectedQuery.displayName}`,
      displayName: `${inputValue}`,
      filter: {
        beliggenhedsadresse: {
          adresselinie1: {
            [selectedQuery.query]: inputValue,
          },
        },
      },
    };

    const updatedSettings = [...settings, settingsItem];

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaVejnavnFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  function removeFilterEventHandler(key: FirmaVejnavnFilterSetting["key"]) {
    const updatedSettings = settings.filter((set) => set.key !== key);

    if (updatedSettings.length === 0) {
      updateFilters({
        event: "delete",
        queryFilter: {
          filterKey: filterKey,
        },
      });
      return;
    }

    const filterString = f<FirmaDtoFilterInput>()
      .addFilter(
        "or",
        updatedSettings.map((set) => set.filter),
      )
      .build();

    // update the query filter
    const queryFilter: QueryFilter<
      FirmaFilterKeys,
      FirmaVejnavnFilterSetting[]
    > = {
      filterKey: filterKey,
      filter: filterString,
      parentFilter: "and",
      settings: updatedSettings,
    };

    updateFilters({
      event: "update",
      queryFilter: queryFilter,
    });
  }

  return (
    <AccordionSubFilterItem
      title="Vejnavn"
      value="vejnavn"
      isActive={!!settings.length}
    >
      <div className="c-applied-filter-group">
        {settings.map((set: FirmaVejnavnFilterSetting) => {
          return (
            <AppliedFilterItem
              key={set.key}
              labelPrefix={set.selectedQueryName}
              label={set.displayName}
              removeFilterHandler={() => {
                removeFilterEventHandler(set.key);
              }}
            />
          );
        })}
      </div>

      <SuperDropdown
        items={data}
        itemsIdKey={"id"}
        itemToString={(item) => item?.vejnavn.navn ?? ""}
        placeholder="Søg efter vejnavne ..."
        asDropdown={false}
        initialInputValue={debouncedInputValue}
        clearInputAfterSelection={true}
        closedIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
        openIcon={isPending ? <Spinner inline size={14} /> : <SearchIcon />}
        itemToElement={(item) => (
          <DefaultDropdownItem label={item.vejnavn.navn} />
        )}
        onStateChange={(changes) => {
          if (changes.inputValue !== undefined) {
            const inputValue = changes.inputValue || "";
            setQuery(inputValue);
          }
        }}
        onSelectedItemEvent={(item) => {
          if (item.selectedItem) {
            inputEventHandler(item.selectedItem?.vejnavn.navn);
          }
        }}
        stateReducer={(_state, actionAndChanges) => {
          const { type, changes } = actionAndChanges;
          switch (type) {
            case useCombobox.stateChangeTypes.InputKeyDownEnter:
              if (changes.inputValue) {
                inputEventHandler(changes.inputValue);
              }
              return {
                ...changes,
                inputValue: "",
              };
          }
          return { ...changes };
        }}
      />
    </AccordionSubFilterItem>
  );
}
